import React, { useState, useCallback, useEffect, useRef } from 'react';
import CameraModal from '../components/CameraModal';
import BarcodeScanner from '../components/BarcodeScanner';
import '../styles/SearchModal.css';

// step: 'barcode' | 'category' | 'found' | 'photo' | 'add' | 'chz'

const MEDICINE_UNITS = ['мг', 'мкг', 'г', 'кг', 'мл', 'л', 'МЕ', 'ЕД'];
const RU_TO_EN = {
  'й':'q','ц':'w','у':'e','к':'r','е':'t','н':'y','г':'u','ш':'i','щ':'o','з':'p','х':'[','ъ':']',
  'ф':'a','ы':'s','в':'d','а':'f','п':'g','р':'h','о':'j','л':'k','д':'l','ж':';','э':"'",
  'я':'z','ч':'x','с':'c','м':'v','и':'b','т':'n','ь':'m','б':',','ю':'.',
  'Й':'Q','Ц':'W','У':'E','К':'R','Е':'T','Н':'Y','Г':'U','Ш':'I','Щ':'O','З':'P','Х':'{','Ъ':'}',
  'Ф':'A','Ы':'S','В':'D','А':'F','П':'G','Р':'H','О':'J','Л':'K','Д':'L','Ж':':','Э':'"',
  'Я':'Z','Ч':'X','С':'C','М':'V','И':'B','Т':'N','Ь':'M','Б':'<','Ю':'>',
};
function remapLayout(str) {
  return str.split('').map(ch => RU_TO_EN[ch] !== undefined ? RU_TO_EN[ch] : ch).join('');
}


const MEDICINE_FORMS = [
  { label: 'Таблетки', formType: 'solid' },
  { label: 'Капсулы', formType: 'solid' },
  { label: 'Порошок', formType: 'solid' },
  { label: 'Гранулы', formType: 'solid' },
  { label: 'Раствор', formType: 'liquid' },
  { label: 'Суспензия', formType: 'liquid' },
  { label: 'Сироп', formType: 'liquid' },
  { label: 'Капли', formType: 'liquid' },
  { label: 'Спрей', formType: 'liquid' },
  { label: 'Мазь/Гель/Крем', formType: 'liquid' },
  { label: 'Ампулы', formType: 'ampoule' },
];

function detectFormLabel(chz) {
  if (!chz) return 'Таблетки';
  const formNorm = (chz.form || '').toUpperCase();
  const packNorm = (chz.pack1_name || '').toUpperCase();
  if (formNorm.includes('ТАБЛЕТК')) return 'Таблетки';
  if (formNorm.includes('КАПСУЛ')) return 'Капсулы';
  if (formNorm.includes('ГРАНУЛ')) return 'Гранулы';
  if (formNorm.includes('ПОРОШОК')) return 'Порошок';
  if (packNorm.includes('АМПУЛ') || formNorm.includes('АМПУЛ')) return 'Ампулы';
  if (formNorm.includes('СУСПЕНЗИ')) return 'Суспензия';
  if (formNorm.includes('СИРОП')) return 'Сироп';
  if (formNorm.includes('КАПЛ')) return 'Капли';
  if (formNorm.includes('СПРЕЙ')) return 'Спрей';
  if (formNorm.includes('РАСТВОР')) return 'Раствор';
  if (formNorm.includes('МАЗЬ') || formNorm.includes('ГЕЛЬ') || formNorm.includes('КРЕМ') || formNorm.includes('ЛИНИМЕНТ')) return 'Мазь/Гель/Крем';
  if (chz.formType === 'ampoule') return 'Ампулы';
  if (chz.formType === 'liquid') return 'Раствор';
  return 'Таблетки';
}

function getMedicineVolumeUnit(chz) {
  if (!chz) return { volume: null, unit: 'мг' };
  const formType = chz.formType || 'solid';
  if (formType === 'solid') {
    const m = String(chz.dosage || '').match(/^([\d.,]+)\s*(\S+)/);
    if (m) {
      const value = parseFloat(m[1].replace(',', '.'));
      const unit = m[2];
      if (!isNaN(value)) return { volume: value, unit: MEDICINE_UNITS.includes(unit) ? unit : 'мг' };
    }
    return { volume: null, unit: 'мг' };
  }
  // liquid / ampoule: pack1_size = container volume
  const vol = chz.pack1_size ? parseFloat(String(chz.pack1_size).replace(',', '.')) : null;
  const formNorm = (chz.form || '').toUpperCase();
  const unit = formNorm.includes('ПОРОШОК') ? 'г' : 'мл'; // ПОРОШОК=г, else мл
  return { volume: (vol && !isNaN(vol)) ? vol : null, unit };
}

const playBeep = (found) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (found) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 1400; osc.type = 'square';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.15);
    } else {
      [0, 0.18].forEach(offset => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 400; osc.type = 'square';
        gain.gain.setValueAtTime(0.2, ctx.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.12);
        osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.12);
      });
    }
  } catch (e) {}
};

const playTripleBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.15, 0.30].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 800; osc.type = 'square';
      gain.gain.setValueAtTime(0.25, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.1);
      osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.1);
    });
  } catch (e) {}
};

function formatExpiry(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return mm + '/' + yyyy;
  } catch (e) {
    return dateStr;
  }
}

function speak(text) {
  try {
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'ru-RU';
    utt.rate = 1.05;
    window.speechSynthesis.speak(utt);
  } catch (e) {}
}

function ArrivalModal({ isOpen, onClose, onDone, onAutoTab }) {
  const [step, setStep] = useState('barcode');
  const [scannerActive, setScannerActive] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannedBarcode, setScannedBarcode] = useState('');
  const barcodeInputRef = useRef(null);
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    try { return localStorage.getItem('arrival_tts') !== 'off'; } catch { return true; }
  });

  const [cameraOpen, setCameraOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [foundItem, setFoundItem] = useState(null);
  const [foundSection, setFoundSection] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (ttsEnabled && foundItem && step === 'found') {
      const qty = foundItem.quantity ?? 0;
      speak(String(qty));
    }
  }, [foundItem, step]);

  const [newName, setNewName] = useState('');
  const [newVolume, setNewVolume] = useState('');
  const [newSectionId, setNewSectionId] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [photoFilename, setPhotoFilename] = useState(null);

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [internalCategory, setInternalCategory] = useState(null);
  const [internalSections, setInternalSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [sectionCreating, setSectionCreating] = useState(false);

  // CHZ state
  const [chzData, setChzData] = useState(null);
  const [chzEditedName, setChzEditedName] = useState('');
  const [chzPhotos, setChzPhotos] = useState([]);
  const [chzCameraOpen, setChzCameraOpen] = useState(false);
  const [volumeUnit, setVolumeUnit] = useState('мг');
  const [selectedForm, setSelectedForm] = useState('Таблетки');
  const [pack1Count, setPack1Count] = useState(1);
  const [pack2Count, setPack2Count] = useState(1);
  const [showPackHelp, setShowPackHelp] = useState(false);
  const [editCisProducer, setEditCisProducer] = useState('');
  const [editCisBatch, setEditCisBatch] = useState('');
  const [editCisExpiry, setEditCisExpiry] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('barcode');
      setScannerActive(true);
      setBarcodeInput('');
      setScannedBarcode('');
      setFoundItem(null);
      setFoundSection(null);
      setPhotos([]);
      setPhotoFilename(null);
      setQuantity(1);
      setNewName('');
      setNewVolume('');
      setNewSectionId('');
      setNewUnit('');
      setSavedMsg('');
      setInternalCategory(null);
      setInternalSections([]);
      setChzData(null);
      setChzPhotos([]);
      setVolumeUnit('мг');
      setSelectedForm('Таблетки');
      setPack1Count(1);
      setPack2Count(1);
      setShowPackHelp(false);
      setEditCisProducer('');
      setEditCisBatch('');
      setEditCisExpiry('');
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadSections = async (cat) => {
    setSectionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory?category=' + cat, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (res.ok) {
        const data = await res.json();
        setInternalSections(data);
      }
    } catch (e) {
      console.error('[ArrivalModal] loadSections error:', e);
    } finally {
      setSectionsLoading(false);
    }
  };

  const handleCreateSection = async () => {
    if (!newSectionName.trim()) return;
    setSectionCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/sections', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newSectionName.trim(), category: internalCategory }),
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      setInternalSections(prev => [...prev, created]);
      setNewSectionId(created.sync_id);
      setCreatingSection(false);
      setNewSectionName('');
    } catch (err) {
      console.error('[ArrivalModal] createSection error:', err);
      setSavedMsg('Ошибка создания секции.');
    } finally {
      setSectionCreating(false);
    }
  };

  const lookupBarcode = async (code) => {
    if (!code) return;
    if (step === 'chz' && chzData && !chzData.found_by_cis) { playTripleBeep(); return; }
    setScannerActive(false);
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/barcode/' + encodeURIComponent(code), {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setScannedBarcode(code);
      if (data.found) {
        playBeep(true);
        const item = data;
        setFoundItem(item);
        setFoundSection({ name: data.section_name, sync_id: data.section_sync_id } || null);
        if (data.category) {
          setInternalCategory(data.category);
          loadSections(data.category);
        }
        setSelectedForm(item.form || 'Таблетки');
        setEditCisExpiry(item.expiry_date ? String(item.expiry_date).slice(0, 10) : '');
        setEditCisProducer(item.producer || '');
        setEditCisBatch(item.batch || '');
        setNewVolume(item.volume_value != null ? String(item.volume_value) : '');
        setVolumeUnit(item.unit || 'мг');
        setPack1Count(item.pack1_count || 1);
        setPack2Count(item.pack2_count || 1);
        setNewSectionId(data.section_sync_id || '');
        setQuantity(1);
        setStep('found');
      } else {
        playBeep(false);
        setStep('category');
      }
    } catch (err) {
      console.error('[ArrivalModal] barcode lookup error:', err);
      setSavedMsg('Ошибка поиска. Попробуйте ещё раз.');
      setScannerActive(true);
    } finally {
      setLoading(false);
    }
  };

  const lookupChestnyZnak = async (code) => {
    if (step === 'chz' && chzData && !chzData.found_by_cis) { playTripleBeep(); return; }
    setScannerActive(false);
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/inventory/chestny-znak', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.found === false) {
        playBeep(false);
        setSavedMsg('Товар не найден в Честном знаке или не является лекарством.');
        setScannerActive(true);
        setLoading(false);
        return;
      }
      playBeep(true);
      setChzData(data);
      setChzEditedName((data.chz && data.chz.productName) || (data.existing_item && data.existing_item.name) || '');
      setSelectedForm(detectFormLabel(data.chz));
      setInternalCategory('medicine');
      if (onAutoTab) onAutoTab('medicine');
      if (!data.found_by_cis) {
        const { volume, unit } = getMedicineVolumeUnit(data.chz);
        setVolumeUnit(data.chz?.unit || unit || 'мг');
        setNewVolume(data.chz?.volume_value != null ? String(data.chz.volume_value) : (volume != null ? String(volume) : ''));
        setPack1Count(data.chz?.pack1_count > 1 ? data.chz.pack1_count : 1);
        setPack2Count(data.chz?.pack2_count > 1 ? data.chz.pack2_count : 1);
        setEditCisExpiry(data.chz?.expiry_date ? String(data.chz.expiry_date).slice(0, 10) : '');
        setNewSectionId(data.section_sync_id || '');
        loadSections('medicine');
        if (ttsEnabled) setTimeout(() => speak('не найдено'), 200);
      } else if (data.existing_item) {
        setNewVolume(data.existing_item.volume_value != null ? String(data.existing_item.volume_value) : '');
        setVolumeUnit(data.existing_item.unit || 'мг');
        setPack1Count(data.existing_item.pack1_count || 1);
        setPack2Count(data.existing_item.pack2_count || 1);
        if (data.existing_item.form) setSelectedForm(data.existing_item.form);
        setNewSectionId(data.existing_item.section_sync_id || '');
        loadSections('medicine');
        setEditCisProducer(data.existing_item.producer || '');
        setEditCisBatch(data.existing_item.batch || '');
        setEditCisExpiry(data.existing_item.expiry_date ? (typeof data.existing_item.expiry_date === 'string' ? data.existing_item.expiry_date.slice(0, 10) : new Date(data.existing_item.expiry_date).toISOString().slice(0, 10)) : '');
      }
      setStep('chz');
    } catch (err) {
      console.error('[ArrivalModal] chz lookup error:', err);
      setSavedMsg('Ошибка запроса к Честному знаку.');
      setScannerActive(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeDetected = useCallback(({ code, isQR }) => {
    console.log('[ArrivalModal] scanned isQR:', isQR, 'code:', code.substring(0, 60));
    if (isQR) {
      lookupChestnyZnak(code);
    } else {
      lookupBarcode(code);
    }
  }, []);

  const handleBarcodeManual = () => {
    const code = barcodeInput.trim();
    if (!code) return;
    // Debug: log raw scan input to server
    fetch('/api/inventory/debug-scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ raw: code })
    }).catch(() => {});
    // GS1 DataMatrix (Честный знак): содержит \x1d или начинается с 01+14цифр+21
    let processCode = code;
    if (processCode.includes('\x1d') || /^01\d{14}21/.test(processCode)) {
      if (!processCode.includes('\x1d')) {
        // Нет GS-разделителей (русская раскладка или браузер отбросил \x1d).
        if (/[а-яёА-ЯЁ]/.test(processCode)) {
          // Русская раскладка: сначала отрезаем кириллицу
          const m = processCode.match(/^[\x00-\x7F]+/);
          processCode = m ? m[0] : processCode;
        }
        // Серийный номер ЧЗ — ровно 13 символов после AI "21", дальше 91/92 не нужны API
        const m = processCode.match(/^(01\d{14}21.{13})/);
        if (m) processCode = m[1];
      }
      lookupChestnyZnak(processCode);
    } else {
      lookupBarcode(processCode);
    }
  };

  const handleCategorySelect = async (cat) => {
    setInternalCategory(cat);
    await loadSections(cat);
    setNewSectionId('');
    setStep('photo');
  };

  const handleCameraCapture = useCallback((items) => {
    const imageItems = items.filter(i => i.type === 'image');
    setPhotos(prev => [...prev, ...imageItems].slice(0, 3));
    setCameraOpen(false);
  }, []);

  const handleChzCameraCapture = useCallback((items) => {
    const imageItems = items.filter(i => i.type === 'image');
    setChzPhotos(prev => [...prev, ...imageItems].slice(0, 1));
    setChzCameraOpen(false);
  }, []);

  const removePhoto = (idx) => {
    setPhotos(prev => {
      prev[idx] && URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleIdentify = async () => {
    if (photos.length === 0) return;
    setLoading(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      photos.forEach(p => formData.append('photos', p.file));
      const res = await fetch('/api/inventory/identify', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPhotoFilename(data.photo_filename || null);
      setNewName(data.name || '');
      setNewVolume(data.volume_value != null ? String(data.volume_value) : '');
      setNewUnit(data.unit || '');
      setNewSectionId('');
      setStep('add');
    } catch (err) {
      console.error('[ArrivalModal] identify error:', err);
      setSavedMsg('Ошибка определения. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleArriveFound = async () => {
    if (!foundItem?.sync_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (internalCategory === 'medicine') {
        await fetch('/api/inventory/items/' + foundItem.sync_id + '/fields', {
          method: 'PATCH',
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form: selectedForm || null,
            producer: editCisProducer || null,
            batch: editCisBatch || null,
            expiry_date: editCisExpiry || null,
            volume_value: newVolume !== '' ? parseFloat(newVolume) : null,
            unit: volumeUnit || null,
            pack1_count: pack1Count,
            pack2_count: pack2Count,
          }),
        });
      }
      const body = {
        found: true,
        sync_id: foundItem.sync_id,
        category: internalCategory,
        quantity: parseInt(quantity) || 1,
        barcode: scannedBarcode || undefined,
        photo_filename: photoFilename || undefined,
      };
      const res = await fetch('/api/inventory/arrive', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Приход записан!');
      setTimeout(() => { onDone && onDone(); handleClose(); }, 900);
    } catch (err) {
      console.error('[ArrivalModal] arrive found error:', err);
      setSavedMsg('Ошибка сохранения.');
    } finally {
      setSaving(false);
    }
  };

  const handleArriveNew = async () => {
    if (!newName.trim() || !newSectionId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const sec = internalSections.find(s => s.sync_id === newSectionId);
      const uc = sec?.unit_conversions || [];
      const ratioMap = Object.fromEntries(uc.filter(c => c.unit && c.ratio).map(c => [c.unit, c.ratio]));
      const effUnit = newUnit || sec?.unit || '';
      const ratio = ratioMap[effUnit];
      const volumeInBase = (newVolume !== '' && ratio && effUnit !== sec?.unit)
        ? parseFloat(newVolume) / ratio
        : (newVolume !== '' ? parseFloat(newVolume) : null);

      const body = {
        found: false,
        name: newName.trim(),
        section_sync_id: newSectionId,
        volume_value: volumeInBase,
        unit: effUnit || null,
        category: internalCategory,
        quantity: parseInt(quantity) || 1,
        barcode: scannedBarcode || undefined,
        photo_filename: photoFilename || undefined,
        ...(internalCategory === 'medicine' ? {
          form: selectedForm,
          expiry_date: editCisExpiry || null,
          pack1_count: pack1Count,
          pack2_count: pack2Count,
        } : {}),
      };
      const res = await fetch('/api/inventory/arrive', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Приход записан!');
      setTimeout(() => { onDone && onDone(); handleClose(); }, 900);
    } catch (err) {
      console.error('[ArrivalModal] arrive new error:', err);
      setSavedMsg('Ошибка сохранения.');
    } finally {
      setSaving(false);
    }
  };

  const handleArriveMedicine = async () => {
    if (!chzData || !chzData.chz) return;
    setSaving(true);
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const chz = chzData.chz;
      const formType = MEDICINE_FORMS.find(f => f.label === selectedForm)?.formType || 'solid';

      let photoFilenameForMed = chzData.photo_from_barcode || null;
      if (chzPhotos.length > 0) {
        const formData = new FormData();
        formData.append('photo', chzPhotos[0].file);
        const upRes = await fetch('/api/inventory/upload-photo', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        });
        if (upRes.ok) {
          const upData = await upRes.json();
          photoFilenameForMed = upData.photo_filename;
        }
      }

      const body = {
        cis: chz.cis,
        volume_value: newVolume !== '' ? parseFloat(newVolume) : null,
        unit: volumeUnit,
        barcode: chz.barcode || undefined,
        section_sync_id: newSectionId || chzData.section_sync_id || undefined,
        ftg: chz.ftg || undefined,
        productName: chzEditedName || chz.productName,
        producer: chz.producer || undefined,
        expiry_date: editCisExpiry || chz.expiry_date || undefined,
        dosage: chz.dosage || undefined,
        form: selectedForm,
        pack1_count: pack1Count,
        pack2_count: pack2Count,
        pack1_name: chz.pack1_name || undefined,
        pack1_size: chz.pack1_size || undefined,
        batch: chz.batch || undefined,
        annotation: chz.annotation || undefined,
        photo_filename: photoFilenameForMed || undefined,
      };

      const res = await fetch('/api/inventory/arrive-medicine', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Приход записан!');
      setTimeout(() => { onDone && onDone(); handleClose(); }, 900);
    } catch (err) {
      console.error('[ArrivalModal] arrive medicine error:', err);
      setSavedMsg('Ошибка сохранения: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMedicineStatus = async () => {
    if (!chzData?.existing_item?.sync_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        name: chzEditedName.trim() || undefined,
        form: selectedForm || undefined,
        volume_value: newVolume !== '' ? parseFloat(newVolume) : null,
        unit: volumeUnit || null,
        pack1_count: pack1Count,
        pack2_count: pack2Count,
        producer: editCisProducer || null,
        batch: editCisBatch || null,
        expiry_date: editCisExpiry || null,
        section_sync_id: newSectionId || undefined,
      };
      const res = await fetch('/api/inventory/items/' + chzData.existing_item.sync_id + '/fields', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedMsg('Сохранено!');
      setTimeout(() => { onDone && onDone(); handleClose(); }, 800);
    } catch (err) {
      setSavedMsg('Ошибка: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    chzPhotos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]);
    setChzPhotos([]);
    setFoundItem(null);
    setFoundSection(null);
    setPhotoFilename(null);
    setBarcodeInput('');
    setScannedBarcode('');
    setQuantity(1);
    setNewName('');
    setNewVolume('');
    setNewSectionId('');
    setNewUnit('');
    setSavedMsg('');
    setScannerActive(false);
    setChzData(null);
    setVolumeUnit('мг');
    setSelectedForm('Таблетки');
    setPack1Count(1);
    setPack2Count(1);
    setShowPackHelp(false);
    setEditCisProducer('');
    setEditCisBatch('');
    setEditCisExpiry('');
    setStep('barcode');
    onClose();
  };

  if (!isOpen) return null;

  const selectedSection = internalSections.find(s => s.sync_id === newSectionId);
  const availableUnits = (selectedSection?.unit && selectedSection.unit !== 'шт')
    ? [selectedSection.unit, ...(selectedSection.unit_conversions || []).filter(c => c.unit).map(c => c.unit)]
    : [];
  const effectiveUnit = newUnit || selectedSection?.unit || '';

  const titles = { barcode: 'Приход', category: 'Категория', found: 'Найдено', photo: 'Фото товара', add: 'Новая позиция', chz: 'Честный знак' };

  return (
    <div className="sm-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="sm-modal">
        <div className="sm-header">
          <span>{titles[step] || 'Приход'}</span>
          <button
            className="sm-close"
            style={{ fontSize: 18, marginRight: 4, opacity: ttsEnabled ? 1 : 0.35 }}
            title={ttsEnabled ? 'Озвучка включена' : 'Озвучка выключена'}
            onClick={() => {
              setTtsEnabled(v => {
                const next = !v;
                try { localStorage.setItem('arrival_tts', next ? 'on' : 'off'); } catch {}
                return next;
              });
              setTimeout(() => barcodeInputRef.current?.focus(), 0);
            }}
          >🔊</button>
          <button className="sm-close" onClick={handleClose}>X</button>
        </div>

        {step === 'barcode' && (
          <>
            {scannerActive && (
              <BarcodeScanner
                onDetected={handleBarcodeDetected}
                onClose={() => setScannerActive(false)}
              />
            )}
            {loading && <div className="sm-loading">Поиск…</div>}
            {!loading && (
              <>
                <div className="sm-field-row">
                  <input
                    ref={barcodeInputRef}
                    className="sm-text-input"
                    type="text"
                    inputMode="numeric"
                    value={barcodeInput}
                    onChange={e => setBarcodeInput(remapLayout(e.target.value))}
                    onKeyDown={e => e.key === 'Enter' && handleBarcodeManual()}
                    placeholder="Сканируйте или введите код"
                  />
                  <button
                    className="sm-btn sm-btn-search"
                    style={{ width: 'auto', padding: '8px 14px', flexShrink: 0 }}
                    onClick={handleBarcodeManual}
                    disabled={!barcodeInput.trim()}
                  >
                    ОК
                  </button>
                </div>
                <button className="sm-btn-force-new" onClick={() => { setStep('category'); setScannerActive(false); }}>
                  Нет штрихкода — сфотографировать
                </button>
              </>
            )}
          </>
        )}

        {step === 'category' && (
          <div style={{ padding: '8px 0' }}>
            {scannedBarcode && (
              <div className="sm-barcode-hint">
                <div className="sm-barcode-code">{scannedBarcode}</div>
                <div className="sm-barcode-notfound">Не найден в базе</div>
              </div>
            )}
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
              Выберите категорию:
            </div>
            {[['food', '🥫 Корм'], ['medicine', '💊 Лекарства'], ['waste', '🪣 Прочее']].map(([cat, label]) => (
              <button
                key={cat}
                className="sm-btn sm-btn-search"
                style={{ marginBottom: 8, textAlign: 'left' }}
                onClick={() => handleCategorySelect(cat)}
              >{label}</button>
            ))}
          </div>
        )}

        {step === 'found' && foundItem && (
          <div className="sm-result sm-result-found">
            <div className="sm-result-label">Найдено по штрихкоду:</div>
            <div className="sm-found-name">{foundItem.name}</div>
            {internalCategory === 'medicine' && (
              <>
                <div className="sm-field-row">
                  <label>Форма выпуска:</label>
                  <select className="sm-select" style={{ flex: 1 }}
                    value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                    {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                  </select>
                </div>
                <div className="sm-field-row">
                  <label>Годен до:</label>
                  <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                </div>
                <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                  <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="number" min="0" step="0.001" className="sm-qty-input"
                        style={{ width: 64 }} value={newVolume}
                        onChange={e => setNewVolume(e.target.value)} placeholder="0" />
                      <select className="sm-select" style={{ width: 60 }}
                        value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}>
                        {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                      <input type="number" min="1" step="1" className="sm-qty-input"
                        style={{ width: 46 }} value={pack1Count}
                        onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                      <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                      <input type="number" min="1" step="1" className="sm-qty-input"
                        style={{ width: 46 }} value={pack2Count}
                        onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                      <button
                        style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                        onClick={() => setShowPackHelp(v => !v)}>?</button>
                    </div>
                    {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {volumeUnit} всего
                      </div>
                    )}
                    {showPackHelp && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                        <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                        <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                        <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                        <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                        <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div className="sm-qty-row">
              <label>Пришло, шт:</label>
              <input
                type="number"
                min="1"
                className="sm-qty-input"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
              />
            </div>
            <button className="sm-btn sm-btn-save" onClick={handleArriveFound} disabled={saving}>
              {saving ? '…' : 'Записать приход'}
            </button>
            <button className="sm-btn-force-new" onClick={() => {
              setNewName(foundItem.name || '');
              setNewVolume(foundItem.volume_value != null ? String(foundItem.volume_value) : '');
              setNewSectionId(foundSection?.sync_id || '');
              setStep('add');
            }}>
              Другая фасовка / новая позиция
            </button>
          </div>
        )}

        {step === 'photo' && (
          <>
            {scannedBarcode && (
              <div className="sm-barcode-hint">
                <div className="sm-barcode-code">{scannedBarcode}</div>
                <div className="sm-barcode-notfound">Не найден в базе</div>
                <div className="sm-barcode-prompt">Сфотографируйте упаковку — штрихкод привяжется автоматически</div>
              </div>
            )}
            <div className="sm-photos-row">
              {photos.map((p, i) => (
                <div key={i} className="sm-photo-thumb">
                  <img src={p.preview} alt="" />
                  <button className="sm-photo-remove" onClick={() => removePhoto(i)}>X</button>
                </div>
              ))}
              {photos.length < 3 && (
                <button className="sm-add-photo" onClick={() => setCameraOpen(true)}>
                  <span>foto</span>
                  <span className="sm-add-label">{photos.length === 0 ? 'Сфотографировать' : 'Ещё фото'}</span>
                </button>
              )}
            </div>
            {photos.length > 0 && !loading && (
              <button className="sm-btn sm-btn-search" onClick={handleIdentify}>Определить</button>
            )}
            {loading && <div className="sm-loading">Определяю…</div>}
            <button className="sm-btn-force-new" onClick={() => {
              setNewName('');
              setNewVolume('');
              setNewSectionId('');
              setNewUnit('');
              setStep('add');
            }}>
              Добавить вручную без фото
            </button>
          </>
        )}

        {step === 'add' && (
          <div className="sm-result sm-result-notfound">
            {sectionsLoading && <div className="sm-loading">Загрузка секций…</div>}
            {!sectionsLoading && (
              <>
                <div className="sm-field-row">
                  <label>Наименование:</label>
                  <input
                    className="sm-text-input"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Название товара"
                    autoFocus
                  />
                </div>
                <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                  <label style={{ paddingTop: '8px' }}>Секция:</label>
                  <div className="sm-section-picker">
                    <div className="sm-section-display" style={!newSectionId ? { borderColor: '#c03030', color: '#ff6666' } : {}}>
                      {selectedSection?.name || '— выберите секцию —'}
                      <span className="sm-section-arrow">v</span>
                    </div>
                    <select
                      className="sm-section-select"
                      value={creatingSection ? '__new__' : newSectionId}
                      onChange={e => {
                        if (e.target.value === '__new__') {
                          setCreatingSection(true);
                          setNewSectionId('');
                        } else {
                          setCreatingSection(false);
                          setNewSectionId(e.target.value);
                          setNewUnit('');
                        }
                      }}
                    >
                      <option value="" disabled>— выберите секцию —</option>
                      {internalSections.map(s => (
                        <option key={s.sync_id} value={s.sync_id}>{s.name}</option>
                      ))}
                      <option value="__new__">+ Новая секция</option>
                    </select>
                  </div>
                </div>
                {creatingSection && (
                  <div className="sm-field-row" style={{ gap: 6 }}>
                    <input
                      className="sm-text-input"
                      style={{ flex: 1 }}
                      placeholder="Название новой секции"
                      value={newSectionName}
                      onChange={e => setNewSectionName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreateSection()}
                      autoFocus
                    />
                    <button
                      className="sm-btn sm-btn-search"
                      style={{ width: 'auto', padding: '8px 14px', flexShrink: 0 }}
                      onClick={handleCreateSection}
                      disabled={sectionCreating || !newSectionName.trim()}
                    >
                      {sectionCreating ? '…' : 'Создать'}
                    </button>
                  </div>
                )}
                {!newSectionId && !creatingSection && (
                  <div style={{ color: '#ff6666', fontSize: 12, marginTop: -6, marginBottom: 4 }}>
                    Обязательно выберите секцию
                  </div>
                )}
                {internalCategory === 'medicine' && (
                  <div className="sm-field-row">
                    <label>Форма выпуска:</label>
                    <select className="sm-select" style={{ flex: 1 }}
                      value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                      {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                    </select>
                  </div>
                )}
                {internalCategory === 'medicine' ? (
                  <>
                    <div className="sm-field-row">
                      <label>Годен до:</label>
                      <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                    </div>
                    <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                      <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input type="number" min="0" step="0.001" className="sm-qty-input"
                            style={{ width: 64 }} value={newVolume}
                            onChange={e => setNewVolume(e.target.value)} placeholder="0" />
                          <select className="sm-select" style={{ width: 60 }}
                            value={newUnit || 'мл'} onChange={e => setNewUnit(e.target.value)}>
                            {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                          <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                          <input type="number" min="1" step="1" className="sm-qty-input"
                            style={{ width: 46 }} value={pack1Count}
                            onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                          <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                          <input type="number" min="1" step="1" className="sm-qty-input"
                            style={{ width: 46 }} value={pack2Count}
                            onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                          <button
                            style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                            onClick={() => setShowPackHelp(v => !v)}>?</button>
                        </div>
                        {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                            = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {newUnit || 'мл'} всего
                          </div>
                        )}
                        {showPackHelp && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                            <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                            <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                            <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                            <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                            <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : selectedSection?.unit && selectedSection.unit !== 'шт' && (
                  <div className="sm-field-row">
                    <label>Объём:</label>
                    <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="sm-qty-input"
                        style={{ flex: 1 }}
                        value={newVolume}
                        onChange={e => setNewVolume(e.target.value)}
                        placeholder="0"
                      />
                      {availableUnits.length > 1 ? (
                        <select className="sm-select" style={{ width: '70px' }}
                          value={effectiveUnit}
                          onChange={e => setNewUnit(e.target.value)}>
                          {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      ) : (
                        <span style={{ alignSelf: 'center', color: '#888', fontSize: '13px' }}>
                          {selectedSection.unit}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="sm-field-row">
                  <label>Пришло, шт:</label>
                  <input
                    type="number"
                    min="1"
                    className="sm-qty-input"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                  />
                </div>
                <button
                  className="sm-btn sm-btn-save"
                  onClick={handleArriveNew}
                  disabled={saving || !newName.trim() || !newSectionId || creatingSection}
                >
                  {saving ? '…' : 'Добавить и записать приход'}
                </button>
              </>
            )}
          </div>
        )}

        {step === 'chz' && chzData && (
          <div className="sm-result">
            {loading && <div className="sm-loading">Запрос к Честному знаку…</div>}
            {!loading && (function() {
              const chz = chzData.chz;
              const formType = MEDICINE_FORMS.find(f => f.label === selectedForm)?.formType || 'solid';
              const isFoundByCis = chzData.found_by_cis;
              return (
                <React.Fragment>
                  {!isFoundByCis && (
                    <div style={{ margin: '-4px -4px 12px', padding: '7px 12px', background: '#7a3a00', border: '1px solid #f0a030', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>&#x2795;</span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#f0c060', letterSpacing: 0.5 }}>НОВЫЙ ТОВАР — будет добавлен в базу</span>
                    </div>
                  )}
                  <div style={{ marginBottom: 12 }}>
                    <input
                      value={chzEditedName}
                      onChange={e => setChzEditedName(e.target.value)}
                      style={{ fontWeight: 600, fontSize: 15, color: '#f0a030', background: 'transparent', border: 'none', borderBottom: '1px solid #555', width: '100%', marginBottom: 4, outline: 'none', padding: '2px 0' }}
                    />
                    {chz && chz.form && (
                      <div style={{ color: '#aaa', fontSize: 13 }}>{chz.form}</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    {chz && chz.batch && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        <span style={{ color: '#666' }}>Серия: </span>{chz.batch}
                      </div>
                    )}
                    {chz && chz.expiry_date && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        <span style={{ color: '#666' }}>До: </span>{formatExpiry(chz.expiry_date)}
                      </div>
                    )}
                    {chz && chz.expiry_date && (() => {
                      const exp = new Date(chz.expiry_date);
                      exp.setHours(23, 59, 59, 999);
                      return exp < new Date();
                    })() && (
                      <div style={{
                        marginTop: 8, padding: '8px 12px',
                        background: '#5a1010', border: '1px solid #c03030',
                        borderRadius: 6, color: '#ff6b6b', fontWeight: 600, fontSize: 13
                      }}>
                        ⚠️ Срок годности истёк! Проверьте препарат перед добавлением.
                      </div>
                    )}
                  </div>

                  {chz && chz.producer && (
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                      <span style={{ color: '#666' }}>Производитель: </span>{chz.producer}
                    </div>
                  )}

                  <div className="sm-field-row" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
                    <label style={{ paddingTop: 8 }}>Секция:</label>
                    <div className="sm-section-picker">
                      <div className="sm-section-display">
                        {selectedSection?.name || '— выберите —'}
                        <span className="sm-section-arrow">v</span>
                      </div>
                      <select className="sm-section-select" value={newSectionId} onChange={e => setNewSectionId(e.target.value)}>
                        <option value="" disabled>— выберите секцию —</option>
                        {internalSections.map(s => <option key={s.sync_id} value={s.sync_id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="sm-field-row" style={{ marginBottom: 12 }}>
                    <label>Форма выпуска:</label>
                    <select className="sm-select" style={{ flex: 1 }}
                      value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                      {MEDICINE_FORMS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                    </select>
                  </div>

                  {isFoundByCis ? (
                    <React.Fragment>
                      <div className="sm-field-row">
                        <label>Годен до:</label>
                        <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                      </div>
                      <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                        <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="number" min="0" step="0.001" className="sm-qty-input"
                              style={{ width: 64 }}
                              value={newVolume}
                              onChange={e => setNewVolume(e.target.value)}
                              placeholder="0" />
                            <select className="sm-select" style={{ width: 60 }}
                              value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}>
                              {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack1Count}
                              onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack2Count}
                              onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <button
                              style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                              onClick={() => setShowPackHelp(v => !v)}
                            >?</button>
                          </div>
                          {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {volumeUnit} всего
                            </div>
                          )}
                          {showPackHelp && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                              <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                              <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                              <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                              <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                              <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        className="sm-btn sm-btn-save"
                        onClick={handleSaveMedicineStatus}
                        disabled={saving}
                      >
                        {saving ? '…' : 'Сохранить'}
                      </button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div style={{ marginBottom: 14 }}>
                        {chzData.photo_from_barcode ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img
                              src={'/supply-photos/' + chzData.photo_from_barcode}
                              alt="фото"
                              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
                            />
                            <span style={{ fontSize: 12, color: '#888' }}>Фото из базы</span>
                          </div>
                        ) : (
                          <div>
                            {chzPhotos.length > 0 ? (
                              <div className="sm-photo-thumb" style={{ display: 'inline-block' }}>
                                <img src={chzPhotos[0].preview} alt="" />
                                <button className="sm-photo-remove" onClick={() => {
                                  URL.revokeObjectURL(chzPhotos[0].preview);
                                  setChzPhotos([]);
                                }}>X</button>
                              </div>
                            ) : (
                              <button
                                className="sm-btn sm-btn-photo"
                                onClick={() => setChzCameraOpen(true)}
                              >
                                📷 Сфотографировать
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="sm-field-row" style={{ alignItems: 'flex-start' }}>
                        <label style={{ paddingTop: 6 }}>Доза / упаковка:</label>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="number" min="0" step="0.001" className="sm-qty-input"
                              style={{ width: 64 }}
                              value={newVolume}
                              onChange={e => setNewVolume(e.target.value)}
                              placeholder="0" />
                            <select className="sm-select" style={{ width: 60 }}
                              value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}>
                              {MEDICINE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack1Count}
                              onChange={e => setPack1Count(e.target.value)} onBlur={e => setPack1Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <span style={{ color: '#555', fontWeight: 600 }}>×</span>
                            <input type="number" min="1" step="1" className="sm-qty-input"
                              style={{ width: 46 }}
                              value={pack2Count}
                              onChange={e => setPack2Count(e.target.value)} onBlur={e => setPack2Count(Math.max(1, parseInt(e.target.value) || 1))} />
                            <button
                              style={{ background: 'none', border: '1px solid #555', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#888', fontSize: 11, padding: 0, lineHeight: '18px', flexShrink: 0 }}
                              onClick={() => setShowPackHelp(v => !v)}
                            >?</button>
                          </div>
                          {parseFloat(newVolume) > 0 && (pack1Count > 1 || pack2Count > 1) && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              = {+(parseFloat(newVolume) * pack1Count * pack2Count).toFixed(3)} {volumeUnit} всего
                            </div>
                          )}
                          {showPackHelp && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#1a2a1a', border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>
                              <div style={{ color: '#ccc', fontWeight: 600, marginBottom: 4 }}>Как заполнять:</div>
                              <div>Перекись 100мл: <span style={{ color: '#f0a030' }}>100 мл × 1 × 1</span></div>
                              <div>10 ампул по 2мл: <span style={{ color: '#f0a030' }}>2 мл × 10 × 1</span></div>
                              <div>3 блистера по 5 ампул 2мл: <span style={{ color: '#f0a030' }}>2 мл × 5 × 3</span></div>
                              <div style={{ marginTop: 6, color: '#666' }}>поле 1 — объём/доза единицы<br/>поле 2 — штук в блистере/ряду<br/>поле 3 — блистеров/рядов в пачке</div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="sm-field-row">
                        <label>Годен до:</label>
                        <input type="date" className="sm-text-input" value={editCisExpiry} onChange={e => setEditCisExpiry(e.target.value)} />
                      </div>

                      <button
                        className={'sm-btn ' + (chzPhotos.length > 0 || chzData.photo_from_barcode ? 'sm-btn-save' : 'sm-btn-save-secondary')}
                        onClick={handleArriveMedicine}
                        disabled={saving}
                      >
                        {saving ? '…' : 'Записать и продолжить'}
                      </button>
                    </React.Fragment>
                  )}

                  <button
                    className="sm-btn-force-new"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setChzData(null);
                      setChzPhotos([]);
                      setSelectedForm('Таблетки');
                      setScannerActive(true);
                      setStep('barcode');
                    }}
                  >
                    Сканировать снова
                  </button>
                </React.Fragment>
              );
            })()}
          </div>
        )}

        {savedMsg && <div className="sm-saved-msg">{savedMsg}</div>}
      </div>

      <CameraModal
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
        photoOnly={true}
      />
      <CameraModal
        isOpen={chzCameraOpen}
        onClose={() => setChzCameraOpen(false)}
        onCapture={handleChzCameraCapture}
        photoOnly={true}
      />
    </div>
  );
}

export default ArrivalModal;
